const _ = require("lodash");
const {
	parseNamedOptionalArgs,
	findArguments,
	makeMathCommandNode,
} = require("../util/ParserHelpers");
const StringUtil = require("../../util/StringUtil");
const {drawRoundedRect} = require("../../util/DrawRectangleUtil");

const botHistoryParser = function(ps) {
	let options = {
		noBG: false,
		rowLength: 4,
		align: "left",
		bot: "orthobot",
		fixedWidth: false,
	};
	options = parseNamedOptionalArgs(ps, options);
	const node = makeMathCommandNode("botHistory", findArguments(ps, 1));
	const newNode = {
		type: "botHistory",
		noBackground: StringUtil.ensureBoolStringConvention(options.noBG),
		rowLength: parseInt(options.rowLength) || 4,
		align: options.align,
		children: _.get(node[0], ["children", 0, "children"], []),
		bot: options.bot,
		fixedWidth: options.fixedWidth,
	};
	return newNode;
};

const botHistoryNode = {
	draw(rc, x, y, node, measure) {
		const {
			xPadding,
			yPadding,
			xSpacing,
			ySpacing,
			width,
			top,
			bottom,
			childMeasuresByRow,
			rowMeasures,
			maxChildWidth,
		} = measure;
		const ctx = rc.context;

		const children = node.children || [];
		const {rowLength} = node;
		const rowSize = rowLength;

		if (!node.noBackground) {
			ctx.save();
			// Draw border
			const grad = ctx.createLinearGradient(
				x + 0.38 * width,
				y - 0.88 * (top + bottom),
				x + 0.6 * width,
				y + 1.7 * (top + bottom)
			);
			grad.addColorStop(0, "#68789E");
			grad.addColorStop(1, "#94A2C7");
			ctx.fillStyle = grad;
			const outerRadius = Math.min(xPadding, yPadding);
			drawRoundedRect(ctx, x, y - top, width, top + bottom, outerRadius);
			ctx.fill();

			// Draw screen
			const borderWidth = 0.35 * outerRadius;
			ctx.fillStyle = "#B8E093";
			drawRoundedRect(
				ctx,
				x + borderWidth,
				y + borderWidth - top,
				width - 2 * borderWidth,
				top + bottom - 2 * borderWidth,
				outerRadius - borderWidth
			);
			ctx.fill();

			// Draw bottom left detail
			ctx.beginPath();
			ctx.moveTo(x, y - top + outerRadius);
			ctx.arcTo(x, y + bottom, x + width, y + bottom, outerRadius);
			ctx.lineTo(x + width - outerRadius, y + bottom);
			ctx.strokeStyle = "#506286";
			ctx.stroke();

			// Draw top right detail
			ctx.beginPath();
			ctx.moveTo(x + width, y + bottom - outerRadius);
			ctx.arcTo(x + width, y - top, x, y - top, outerRadius);
			ctx.lineTo(x + outerRadius, y - top);
			ctx.strokeStyle = "#506286";
			ctx.stroke();

			ctx.restore();
		}
		const cRows = _.chunk(children, rowSize);

		let currX = x + xPadding,
			currY = y - top + yPadding;
		cRows.forEach((row, rowIndex) => {
			currX = x + yPadding;
			currY += rowMeasures[rowIndex].top;
			row.forEach((child, col) => {
				const m = childMeasuresByRow[rowIndex][col];
				if (!child.fakeHistoryBotChild) {
					rc.drawNode(
						rc,
						currX + (maxChildWidth - m.width) / 2,
						currY - (rowMeasures[rowIndex].top - m.top),
						child,
						m
					);
				}
				currX += maxChildWidth + xSpacing;
			});
			currY += rowMeasures[rowIndex].bottom + ySpacing;
		});
	},

	measure(rc, fontInfo, props, node) {
		const unit = fontInfo.size * 2.5;

		// padding around edges of content
		const xPadding = unit * 0.2;
		const yPadding = unit * 0.2;

		// space between rows / elements
		const xSpacing = unit * 0.1;
		const ySpacing = unit * 0.1;

		const {children = []} = node;

		if (node.children.length === 0) {
			// Create fake node with some basic measurements
			const fakeChild = {fakeHistoryBotChild: true};
			if (node.bot === "turnbot") {
				fakeChild.width = 1.35 * unit;
				const height = fakeChild.width * (64 / 71);
				fakeChild.top = height / 2;
				fakeChild.bottom = height / 2;
			} else {
				fakeChild.width = 1.45 * unit;
				const height = fakeChild.width * (62 / 58);
				fakeChild.top = height / 2;
				fakeChild.bottom = height / 2;
			}
			node.children.push(fakeChild);
		}

		const {rowLength} = node;
		const rowSize = Math.min(children.length, rowLength);
		const numRows = Math.ceil(children.length / rowSize) || 1;

		const rows = _.chunk(_.clone(children), rowSize);

		let maxChildWidth = 0;
		const measureRow = (nodes, rowIndex) =>
			nodes.map(childNode => {
				if (childNode.fakeHistoryBotChild) {
					maxChildWidth = childNode.width;
					return childNode;
				}
				const nodeMeasure = rc.measureNode(rc, fontInfo, props, childNode);
				maxChildWidth = Math.max(maxChildWidth, nodeMeasure.width);
				return nodeMeasure;
			});
		const childMeasuresByRow = rows.map(measureRow);

		const measuredContentWidth =
			rowSize * (maxChildWidth + xSpacing) - xSpacing;
		const rowMeasures = childMeasuresByRow.map(nodeMeasures => {
			const maxTop = Math.max(...nodeMeasures.map(m => m.top));
			const maxBottom = Math.max(...nodeMeasures.map(m => m.bottom));
			return {
				width: measuredContentWidth,
				top: maxTop,
				bottom: maxBottom,
			};
		});

		let totalWidth = measuredContentWidth + 2 * xPadding;
		if (node.fixedWidth)
			totalWidth = node.rowLength
				? rowLength * (maxChildWidth + xSpacing) - xSpacing + 2 * xPadding
				: unit * 8 + 2 * xPadding;
		const sumOfRowHeights = _.reduce(
			rowMeasures,
			(currTotal, rowMeasure) => {
				return currTotal + (rowMeasure.top + rowMeasure.bottom);
			},
			0
		);
		const totalHeight =
			sumOfRowHeights + (numRows - 1) * ySpacing + 2 * yPadding;
		return {
			xSpacing,
			ySpacing,
			xPadding,
			yPadding,
			width: totalWidth,
			top: totalHeight / 2,
			bottom: totalHeight / 2,
			childMeasuresByRow,
			rowMeasures,
			maxChildWidth,
		};
	},
	readAloud: "",
};

module.exports = {
	parsers: {
		mathCommands: {
			botHistory: botHistoryParser,
		},
	},
	nodes: {
		botHistory: botHistoryNode,
	},
};
